/**
 * InspectionOptionGroup
 *
 * A grid of toggle buttons used for mutually exclusive choices such as
 * Potential (High / Medium / Low), Status, Situation, Type, etc.
 *
 * Two selection modes:
 *   1. Per-option variant – pass `activeVariant` on each option
 *      (e.g. "potential-high"). The button switches to that variant
 *      when selected.
 *   2. Shared active class – pass `activeClass` at group level
 *      (usually `colors.optionActive`). The button keeps the
 *      "card-select" variant but gains the override class when selected.
 */
/** biome-ignore-all lint/suspicious/noExplicitAny: <need to maintain the any option because is a common component> */

import { Button } from '@/components/ui/button'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import { cn } from '@/lib/utils'

const GRID_COLS: Record<number, string> = {
	2: 'grid-cols-2',
	3: 'grid-cols-3',
	4: 'grid-cols-4',
	5: 'grid-cols-5',
}

export interface OptionConfig {
	value: string
	label: string
	/** Shorter label rendered on small screens, when `label` would overflow */
	shortLabel?: string
	/** Button variant to apply when this specific option is selected */
	activeVariant?: string
	/** Extra Tailwind classes applied on hover when the option is NOT selected */
	hoverClass?: string
}

interface InspectionOptionGroupProps {
	label?: string
	value: string
	onChange: (value: string) => void
	options: readonly OptionConfig[]
	/** CSS classes applied to every selected button when activeVariant is not set */
	activeClass?: string
	columns?: 2 | 3 | 4 | 5
}

export const InspectionOptionGroup = ({
	label,
	value,
	onChange,
	options,
	activeClass,
	columns = 3,
}: InspectionOptionGroupProps) => (
	<Field>
		<FieldLabel>{label}</FieldLabel>
		<FieldContent>
			<div className={cn('grid gap-2', GRID_COLS[columns] ?? 'grid-cols-3')}>
				{options.map((opt) => {
					const isSelected = value === opt.value
					return (
						<Button
							key={opt.value}
							type="button"
							variant={
								isSelected && opt.activeVariant
									? (opt.activeVariant as any)
									: 'card-select'
							}
							onClick={() => onChange(opt.value)}
							className={cn(
								'px-3 py-2.5 text-sm font-medium',
								isSelected && !opt.activeVariant && activeClass,
								!isSelected && opt.hoverClass,
							)}
						>
							{opt.shortLabel ? (
								<>
									<span className="sm:hidden">{opt.shortLabel}</span>
									<span className="hidden sm:inline">{opt.label}</span>
								</>
							) : (
								opt.label
							)}
						</Button>
					)
				})}
			</div>
		</FieldContent>
	</Field>
)
